import CloseBtn from "../components/icons/CloseBtn";
import MainContext from "../store/main-context";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Scrollbar, A11y, Autoplay } from 'swiper/modules';
import type { PortfolioItem, SinglePortfolioProps } from "../store/types";
import 'swiper/css';

const POPUP_CLOSE_DELAY_MS = 500;

const SinglePortfolio = ({ showPopup: showPopupProp, closePopup }: SinglePortfolioProps) => {
    const ctx = useContext(MainContext);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
    const [isPopupVisible, setIsPopupVisible] = useState(false);

    const project = useMemo<PortfolioItem | null>(() => {
        if (ctx.activePortfolio === null || ctx.activePortfolio < 0) return null;
        return ctx.portfolio[ctx.activePortfolio] ?? null;
    }, [ctx.activePortfolio, ctx.portfolio]);

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
        setPrefersReducedMotion(mediaQuery.matches);

        const handleChange = (event: MediaQueryListEvent) => {
            setPrefersReducedMotion(event.matches);
        };

        mediaQuery.addEventListener('change', handleChange);
        return () => {
            mediaQuery.removeEventListener('change', handleChange);
        };
    }, []);

    useEffect(() => {
        return () => {
            if (closeTimeoutRef.current) {
                clearTimeout(closeTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (!showPopupProp) {
            setIsPopupVisible(false);
            return;
        }

        let frameId = window.requestAnimationFrame(() => {
            setIsPopupVisible(true);
        });

        return () => {
            window.cancelAnimationFrame(frameId);
        };
    }, [showPopupProp]);

    const hasMultipleImages = Boolean(project && project.images.length > 1);
    const shouldAutoplay = hasMultipleImages && !prefersReducedMotion;

    const renderWebsite = () => {
        if (!project?.website) return null;
        return (
            <>
                <b>Website: &nbsp; </b>{" "}
                <a target="_blank" rel="noopener noreferrer" href={project.website}>
                    Preview
                </a>
                <br></br>
            </>
        );
    };

    return (
        <div className={`portfolio-popup fh-100 side-area-container ${project?.key ?? ''}` + (isPopupVisible ? ' show ' : '')}>
            <div className="box_container w-100  pos-z ">
                <div className="row align-items-center mx-0">
                    <div className="col-lg-2 col-md-4 col-8 mt-md-0 mt-3">
                        <div className="square_ratio border-radius-25">
                            {project && <img
                                src={"/dataImages/" + project?.logo}
                                className="padding_img"
                                alt={project?.title ?? 'Project logo'}
                            />}
                        </div>
                    </div>
                    {project && <div className="col-lg-8 mt-lg-0 mt-4">
                        <h1 className="border-bottom blue text-white pb-3 fw-800">
                            {project?.title}
                        </h1>
                        <p className="fw-700 text-white mt-3 fs-18">
                            <b>Category: &nbsp; </b> {project?.category?.join(' , ')} <br></br>
                            <b>Date: &nbsp; </b> {project?.date} <br></br>
                            <b>Technologies: &nbsp; </b> {project?.technologies?.join(' , ')} <br></br>
                            <b>Information: &nbsp; </b> {project?.info} <br></br>
                            {renderWebsite()}
                        </p>
                    </div>}

                    <div className="col-lg-12 py-4 ">
                        {project && (
                            <Swiper
                                spaceBetween={15}
                                centeredSlides={!hasMultipleImages}
                                slidesPerView={1}
                                slidesPerGroup={1}
                                breakpoints={
                                    hasMultipleImages
                                        ? {
                                            900: {
                                                slidesPerView: 4,
                                                slidesPerGroup: 4,
                                            },
                                        }
                                        : {}
                                }
                                autoplay={shouldAutoplay ? { delay: 5000, disableOnInteraction: false } : false}
                                navigation={hasMultipleImages}
                                pagination={hasMultipleImages ? { clickable: true } : false}
                                modules={[Navigation, Pagination, Scrollbar, Autoplay, A11y]}
                                loop={hasMultipleImages}
                            >
                                {project.images?.map((item, index) => (
                                    <SwiperSlide key={item}>
                                        <img
                                            src={"/dataImages/" + item}
                                            className="w-100"
                                            alt={`${project.title} ${index + 1}`}
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </SwiperSlide>
                                ))}
                            </Swiper>
                        )}
                    </div>
                    <div className="col-12 py-4"></div>
                </div>


            </div>

            <CloseBtn
                togglePopup={() => {
                    closePopup();
                    if (closeTimeoutRef.current) {
                        clearTimeout(closeTimeoutRef.current);
                    }
                    closeTimeoutRef.current = setTimeout(() => {
                        ctx.setActivePortfolio(null);
                    }, POPUP_CLOSE_DELAY_MS);
                }}
                black={false}
            />
        </div>
    );
};

export default SinglePortfolio;
